/**
 * CreateGuardianshipModal — formulário de criação (POST /v1/guardianships).
 *
 * Campos espelham o CreateGuardianshipDto real: wardId/guardianId (IDs de
 * pessoa — honest-defer: person-picker futuro, hoje texto), type (enum REAL,
 * 5 valores ADR-047), scope (checklist do GuardianshipScope canônico), basis
 * (enum), effectiveFrom/Until (datas), documentReferenceId (string).
 *
 * Honest-defers visíveis: auto-cálculo de effectiveUntil e upload real de
 * documento (coletamos a referência como texto).
 */

import { useState } from 'react';

import {
  buildCreateRequest,
  initialGuardianshipForm,
  validateGuardianshipForm,
  type GuardianshipFormState,
} from './guardianship-form';
import {
  GUARDIANSHIP_BASES,
  GUARDIANSHIP_BASIS_LABEL,
  GUARDIANSHIP_SCOPE_FIELDS,
  GUARDIANSHIP_TYPE_LABEL,
  GUARDIANSHIP_TYPES,
  type GuardianshipBasis,
  type GuardianshipType,
} from './types';

interface CreateGuardianshipModalProps {
  onClose: () => void;
  onSubmit: (req: ReturnType<typeof buildCreateRequest>) => void;
  isSubmitting: boolean;
  error: string | null;
}

export function CreateGuardianshipModal({
  onClose,
  onSubmit,
  isSubmitting,
  error,
}: CreateGuardianshipModalProps): JSX.Element {
  const [form, setForm] = useState<GuardianshipFormState>(initialGuardianshipForm);

  function patch(p: Partial<GuardianshipFormState>): void {
    setForm((prev) => ({ ...prev, ...p }));
  }

  function toggleScope(key: (typeof GUARDIANSHIP_SCOPE_FIELDS)[number]['key']): void {
    setForm((prev) => ({ ...prev, scope: { ...prev.scope, [key]: prev.scope[key] !== true } }));
  }

  const validationError = validateGuardianshipForm(form);

  function handleSubmit(): void {
    if (validationError !== null) return;
    onSubmit(buildCreateRequest(form));
  }

  return (
    <div
      className="g-scope g-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains('g-modal-scrim')) onClose();
      }}
    >
      <div className="g-modal" role="dialog" aria-modal="true" aria-label="Nova guardianship">
        <div className="g-modal__head">
          <div>
            <div className="g-modal__title">Nova guardianship</div>
            <div className="g-modal__sub">
              Documentação obrigatória recomendada · audit <code>guardianship.granted</code> gerado
            </div>
          </div>
          <button type="button" className="g-modal__close" aria-label="Fechar" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="g-modal__body">
          <div className="g-field">
            <span className="g-field__label">Tipo de representação</span>
            <div className="g-radio-group" role="radiogroup" aria-label="Tipo de representação">
              {GUARDIANSHIP_TYPES.map((t: GuardianshipType) => (
                <label
                  key={t}
                  className={`g-radio-item${form.type === t ? ' g-radio-item--checked' : ''}`}
                >
                  <input
                    type="radio"
                    name="g-type"
                    className="g-radio-item__input"
                    checked={form.type === t}
                    onChange={() => patch({ type: t })}
                  />
                  <span>
                    <strong className="g-radio-item__title">
                      {GUARDIANSHIP_TYPE_LABEL[t].label}
                    </strong>
                    <span className="g-radio-item__hint">{GUARDIANSHIP_TYPE_LABEL[t].hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="g-form-grid">
            <label className="g-field">
              <span className="g-field__label">ID do representado (ward)</span>
              <input
                className="g-input"
                placeholder="person_…"
                value={form.wardId}
                onChange={(e) => patch({ wardId: e.target.value })}
              />
            </label>
            <label className="g-field">
              <span className="g-field__label">ID do representante (guardian)</span>
              <input
                className="g-input"
                placeholder="person_…"
                value={form.guardianId}
                onChange={(e) => patch({ guardianId: e.target.value })}
              />
            </label>
            <label className="g-field">
              <span className="g-field__label">Base legal</span>
              <select
                className="g-select"
                value={form.basis}
                onChange={(e) => patch({ basis: e.target.value as GuardianshipBasis | '' })}
              >
                <option value="">— não informada —</option>
                {GUARDIANSHIP_BASES.map((b: GuardianshipBasis) => (
                  <option key={b} value={b}>
                    {GUARDIANSHIP_BASIS_LABEL[b]}
                  </option>
                ))}
              </select>
            </label>
            <label className="g-field">
              <span className="g-field__label">Vigência — início</span>
              <input
                type="date"
                className="g-input"
                value={form.effectiveFrom}
                onChange={(e) => patch({ effectiveFrom: e.target.value })}
              />
            </label>
            <label className="g-field">
              <span className="g-field__label">
                Vigência — término <span className="g-defer-tag">auto-cálculo diferido</span>
              </span>
              <input
                type="date"
                className="g-input"
                value={form.effectiveUntil}
                onChange={(e) => patch({ effectiveUntil: e.target.value })}
              />
            </label>
            <label className="g-field">
              <span className="g-field__label">
                Referência do documento <span className="g-defer-tag">upload real diferido</span>
              </span>
              <input
                className="g-input"
                placeholder="doc_… ou referência processual"
                value={form.documentReferenceId}
                onChange={(e) => patch({ documentReferenceId: e.target.value })}
              />
            </label>
          </div>

          <div className="g-field">
            <span className="g-field__label">Escopo da representação</span>
            <div className="g-checkbox-grid">
              {GUARDIANSHIP_SCOPE_FIELDS.map(({ key, label }) => (
                <label key={key} className="g-checkbox">
                  <input
                    type="checkbox"
                    checked={form.scope[key] === true}
                    onChange={() => toggleScope(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {error !== null && (
            <p className="g-alert g-alert--error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="g-modal__foot">
          <button type="button" className="g-btn g-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="g-btn g-btn--primary"
            disabled={validationError !== null || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Registrando…' : 'Registrar guardianship'}
          </button>
        </div>
        {validationError !== null && <span className="g-foot-hint">{validationError}</span>}
      </div>
    </div>
  );
}
